import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  Warning as DuplicateIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  CloudUpload as ImportIcon,
} from '@mui/icons-material';
import DuplicateResolutionPanel from './DuplicateResolutionPanel';

const STATUS_CONFIG = {
  ready: {
    icon: ReadyIcon,
    label: 'Ready',
    color: 'success',
    chipColor: 'success',
  },
  duplicate: {
    icon: DuplicateIcon,
    label: 'Duplicate',
    color: 'warning',
    chipColor: 'warning',
  },
  error: {
    icon: ErrorIcon,
    label: 'Error',
    color: 'error',
    chipColor: 'error',
  },
};

const ImportReviewModal = ({ open, onClose, importedDirectors, onImport, onResolve }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [directors, setDirectors] = useState(importedDirectors);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const ready = directors.filter(d => d.status === 'ready').length;
    const duplicates = directors.filter(d => d.status === 'duplicate').length;
    const errors = directors.filter(d => d.status === 'error').length;
    return { total: directors.length, ready, duplicates, errors };
  }, [directors]);

  // Filter and search directors
  const filteredDirectors = useMemo(() => {
    let result = directors;

    // Apply status filter
    if (filter !== 'all') {
      result = result.filter(d => d.status === filter);
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(d => 
        d.firstName?.toLowerCase().includes(search) ||
        d.lastName?.toLowerCase().includes(search) ||
        d.email?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [directors, filter, searchTerm]);

  const handleResolve = (director) => {
    setSelectedDirector(director);
  };

  const handleResolutionComplete = (resolution) => {
    // Update the director's status based on resolution
    setDirectors(prevDirectors => 
      prevDirectors.map(d => 
        d._rowIndex === resolution.directorIndex 
          ? { ...d, status: 'ready', resolution }
          : d
      )
    );
    setSelectedDirector(null);
  };

  const handleImportReady = () => {
    const readyDirectors = directors.filter(d => d.status === 'ready');
    onImport(readyDirectors);
  };

  const handleImportAll = () => {
    if (summary.duplicates === 0 && summary.errors === 0) {
      onImport(directors);
    }
  };

  const canImportAll = summary.duplicates === 0 && summary.errors === 0;
  const issueCount = summary.duplicates + summary.errors;

  return (
    <>
      <Dialog 
        open={open && !selectedDirector} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Director Import Review</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Summary Stats */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Box display="flex" gap={3} justifyContent="center" alignItems="center">
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {summary.total}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">
                  {summary.ready}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Ready
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">
                  {summary.duplicates}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Duplicates
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main">
                  {summary.errors}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Errors
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Filters and Search */}
          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Filter"
              >
                <MenuItem value="all">All ({summary.total})</MenuItem>
                <MenuItem value="ready">Ready ({summary.ready})</MenuItem>
                <MenuItem value="duplicate">Duplicates ({summary.duplicates})</MenuItem>
                <MenuItem value="error">Errors ({summary.errors})</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Directors Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="50">#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Passport/Emirates ID</TableCell>
                  <TableCell width="150">Status</TableCell>
                  <TableCell width="100" align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDirectors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">
                        No directors found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDirectors.map((director) => {
                    const statusConfig = STATUS_CONFIG[director.status];
                    const StatusIcon = statusConfig.icon;

                    return (
                      <TableRow key={director._rowIndex} hover>
                        <TableCell>{director._rowIndex + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {director.firstName} {director.lastName}
                          </Typography>
                          {director.dateOfBirth && (
                            <Typography variant="caption" color="textSecondary">
                              DOB: {director.dateOfBirth}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {director.email || '-'}
                          </Typography>
                          {director.phone && (
                            <Typography variant="caption" color="textSecondary">
                              {director.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {director.passportNumber && (
                            <Typography variant="caption" display="block">
                              Passport: {director.passportNumber}
                            </Typography>
                          )}
                          {director.emiratesId && (
                            <Typography variant="caption" display="block">
                              Emirates ID: {director.emiratesId}
                            </Typography>
                          )}
                          {!director.passportNumber && !director.emiratesId && '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<StatusIcon />}
                            label={statusConfig.label}
                            color={statusConfig.chipColor}
                            size="small"
                          />
                          {director.errors && director.errors.length > 0 && (
                            <Tooltip title={director.errors.join(', ')}>
                              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                {director.errors.length} error(s)
                              </Typography>
                            </Tooltip>
                          )}
                          {director.matches && director.matches.length > 0 && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                              {director.matches.length} match(es)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {(director.status === 'duplicate' || director.status === 'error') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleResolve(director)}
                            >
                              Resolve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Warning message */}
          {issueCount > 0 && (
            <Box mt={2}>
              <Chip
                icon={<ErrorIcon />}
                label={`Please resolve ${issueCount} issue${issueCount > 1 ? 's' : ''} before importing all directors`}
                color="warning"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel Import
          </Button>
          {summary.ready > 0 && (
            <Button
              variant="outlined"
              onClick={handleImportReady}
              startIcon={<ImportIcon />}
            >
              Import Ready Only ({summary.ready})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleImportAll}
            disabled={!canImportAll}
            startIcon={<ImportIcon />}
          >
            Import All ({summary.total})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Resolution Panel */}
      {selectedDirector && (
        <DuplicateResolutionPanel
          open={!!selectedDirector}
          director={selectedDirector}
          onClose={() => setSelectedDirector(null)}
          onResolve={handleResolutionComplete}
        />
      )}
    </>
  );
};

export default ImportReviewModal;
